import React, { useMemo, useState } from 'react'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
    useTheme,
} from '@material-ui/core'

import { Container } from '../../../../components'
import { TemplateService } from '../../../../models'
import { useAutoScheduleStyles } from './styles'

interface UnitInspectionRow {
    id: number
    complete_status: boolean
    unit_id: number
    unit_name?: string
}

interface Props {
    services: TemplateService[]
    unitInspections: UnitInspectionRow[]
    canFillDemo: boolean
    onFillDemo: () => Promise<void>
    onContinue: () => void
}

export const WalkSchedulerProgress = ({
    services,
    unitInspections,
    canFillDemo,
    onFillDemo,
    onContinue,
}: Props) => {
    const classes = useAutoScheduleStyles()
    const theme = useTheme()

    const openUnits = useMemo(() => {
        return unitInspections
            .filter((unit) => !unit.complete_status)
            .slice()
            .sort((left, right) =>
                (left.unit_name ?? '').localeCompare(right.unit_name ?? ''),
            )
    }, [unitInspections])

    const completeCount = unitInspections.length - openUnits.length
    const inspectionIncomplete =
        unitInspections.length === 0 || openUnits.length > 0
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [filling, setFilling] = useState(false)
    const showFillDemo = canFillDemo && openUnits.length > 0

    const fillAndContinue = async () => {
        setFilling(true)
        try {
            await onFillDemo()
            setConfirmOpen(false)
            onContinue()
        } catch {
            // The caller reports the failure.
        } finally {
            setFilling(false)
        }
    }

    const continueToSchedule = () => {
        if (inspectionIncomplete) {
            setConfirmOpen(true)
            return
        }
        onContinue()
    }

    return (
        <Container
            className={classes.root}
            style={{ padding: theme.spacing(2, 4, 4) }}
        >
            <Container
                className={classes.walkSetup}
                style={{ padding: theme.spacing(1, 2, 2) }}
            >
                <div style={{ padding: theme.spacing(1, 0, 1) }}>
                    <Typography variant="h6" className={classes.walkSetupTitle}>
                        Walk progress
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Services and vendors saved for this walk.
                    </Typography>
                </div>
                <Container
                    className={classes.servicesScrollContainer}
                    scrollY
                    style={{ flex: 1, minHeight: 0 }}
                >
                    {services.length === 0 && (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            className={classes.walkEmptyCopy}
                        >
                            No services are saved for this walk.
                        </Typography>
                    )}
                    {services.map((templateService) => {
                        const vendorNames = templateService.template_service_vendors
                            .map((vendor) => vendor.vendor.name)
                            .filter(Boolean)
                        return (
                            <div
                                key={templateService.id}
                                className={classes.walkSummaryService}
                            >
                                <Typography className={classes.walkSummaryName}>
                                    {templateService.service.name}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {vendorNames.length > 0
                                        ? vendorNames.join(', ')
                                        : 'No vendor'}
                                </Typography>
                            </div>
                        )
                    })}
                    <div style={{ padding: theme.spacing(2, 0, 1) }}>
                        <Typography
                            variant="h6"
                            className={classes.walkSetupTitle}
                        >
                            {unitInspections.length === 0
                                ? 'No units on this inspection yet'
                                : `${completeCount} of ${unitInspections.length} units complete`}
                        </Typography>
                    </div>
                    {openUnits.length > 0 && (
                        <div className={classes.walkUnitList}>
                            <Typography variant="body2" color="textSecondary">
                                Still open
                            </Typography>
                            {openUnits.map((unit) => (
                                <Typography key={unit.id} variant="body2">
                                    {unit.unit_name ?? `Unit ${unit.unit_id}`}
                                </Typography>
                            ))}
                        </div>
                    )}
                </Container>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={continueToSchedule}
                    style={{
                        width: '100%',
                        maxWidth: 300,
                        alignSelf: 'flex-start',
                    }}
                >
                    Continue to scheduling
                </Button>
                {inspectionIncomplete && (
                    <Typography variant="body2" color="textSecondary">
                        Units that are still open are left out of the schedule.
                    </Typography>
                )}
            </Container>
            <Dialog
                open={confirmOpen}
                onClose={() => {
                    if (!filling) setConfirmOpen(false)
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Continue with an unfinished inspection?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Only continue if you are done with the inspection. Units
                        you have not inspected yet will not be scheduled.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        disabled={filling}
                    >
                        Go back
                    </Button>
                    {showFillDemo && (
                        <Button
                            color="primary"
                            variant="outlined"
                            onClick={fillAndContinue}
                            disabled={filling}
                        >
                            {filling
                                ? 'Filling…'
                                : 'Fill with demo data and continue'}
                        </Button>
                    )}
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={filling}
                        onClick={() => {
                            setConfirmOpen(false)
                            onContinue()
                        }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
